import React, { useContext } from 'react';
import { useDrag } from 'react-dnd';
import { Flex } from 'antd';

import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { Task, TASK_TYPE_TASK } from '@totem/components/ticketing/ticketing';
import NaPartyInvolvedContext from '@totem/components/unauthenticated/ticketing/details/partyInvolved/naPartyInvolvedContext';
import NaTaskActionMenu from '@totem/components/unauthenticated/ticketing/details/tasks/naTaskActionMenu';
import IBDIcon from '@totem/styles/Icon';
import { DateStringToLocal } from '@totem/utilities/dateUtilities';

const showResourcesForStatus = ['PENDING', 'IN PROGRESS', 'SCHEDULED'];

type Props = {
  color: string;
  task: Task;
};

const NaTaskCard = ({ color, task }: Props) => {
  const { partyInvolved } = useContext(NaPartyInvolvedContext);
  const { ticket } = useContext(TicketContext);
  const { tasks } = useContext(TaskContext);
  const [, drag] = useDrag(() => ({
    type: TASK_TYPE_TASK,
    item: task,
    canDrag:
      task.type === TASK_TYPE_TASK &&
      task.assignedResources.includes(partyInvolved.id),
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const getResourceNames = () => {
    const resourceNames = [];

    task.assignedResources.forEach((assignedResource) => {
      ticket.ticket.partiesInvolved.forEach((pi) => {
        if (pi.id === assignedResource) {
          if (!resourceNames.includes(pi.name)) {
            resourceNames.push(pi.name);
          }
        }
      });
    });

    return resourceNames;
  };

  const getResourcesDisplay = () => {
    const resourceNames = getResourceNames();
    if (resourceNames.length > 0) {
      if (resourceNames.length === 1) {
        return (
          <div style={{ paddingLeft: '32px' }}>
            Resource: {resourceNames[0]}
          </div>
        );
      }
      return (
        <>
          <div style={{ paddingLeft: '32px' }}>Resources:</div>
          {resourceNames.map((resourceName) => (
            <div key={resourceName} style={{ paddingLeft: '40px' }}>
              {resourceName}:
            </div>
          ))}
        </>
      );
    }
    return <span />;
  };

  const getDependencies = () => {
    const dependencyNumbers = task.dependencies.map((dep) => dep.taskId);
    const dependencies = tasks.filter(
      (chk) =>
        dependencyNumbers.includes(chk.number) &&
        chk.status !== 'COMPLETED' &&
        chk.status !== 'CANCELLED',
    );
    return (
      <>
        {dependencies.map((dep) => (
          <div key={dep.id}>
            Task: {dep.number}: {dep.status}
          </div>
        ))}
      </>
    );
  };

  return (
    <div
      ref={drag}
      style={{
        borderStyle: 'solid',
        borderWidth: 2,
        borderColor: color,
        marginBottom: 8,
        padding: '4px',
      }}
    >
      <div style={{ fontWeight: 700 }}>
        <Flex justify={'space-between'} align={'flex-start'}>
          <div>
            <IBDIcon name={task.type} style={{ marginRight: '16px' }} />
            {task.number}: {task.name}
          </div>
          <div>
            <NaTaskActionMenu task={task} />
          </div>
        </Flex>
      </div>
      {task.status === 'SCHEDULED' && (
        <div style={{ paddingLeft: '32px' }}>
          Scheduled Start: {DateStringToLocal(task.expectedStartDate)}
        </div>
      )}
      {task.status === 'IN PROGRESS' && (
        <div style={{ paddingLeft: '32px' }}>
          Started: {DateStringToLocal(task.actualStartDate)}
        </div>
      )}
      {task.status === 'COMPLETED' && (
        <div style={{ paddingLeft: '32px' }}>
          Completed: {DateStringToLocal(task.actualEndDate)}
        </div>
      )}
      {showResourcesForStatus.includes(task.status) && getResourcesDisplay()}
      {task.status === 'BLOCKED' && (
        <div style={{ paddingLeft: '32px' }}>
          Dependencies:
          <div style={{ paddingLeft: '16px' }}>{getDependencies()}</div>
        </div>
      )}
    </div>
  );
};

export default NaTaskCard;
